/* =====================================================================
   The Latest Drop — PDP enhancements (additive, vanilla JS)
   Inflection Growth
   ---------------------------------------------------------------------
   Three jobs, all progressive enhancement over the stock Palo Alto PDP:
     1. Auto-select the first in-stock variant on load, so a sold-out
        first variant no longer makes an in-stock listing look dead.
     2. Move the "Width" selector to the top of the variant stack
        (width gates size/color availability for TLD's listings).
     3. Show the selected color's name next to the swatches (opt-in).

   It drives the theme's OWN selectors (same inputs/events theme.js
   listens for), so price, media, [name=id] and the Add-to-Cart button
   update through the native variant logic — we add no parallel state.

   Coordinating with the Rubik variant app:
     If Rubik's "Choose available option dynamically" is enabled, set
     window.TLD_DISABLE_AUTOSELECT = true (or data-tld-autoselect="off"
     on the section) to avoid both selecting at once. Keep ONE on.
   ===================================================================== */
(function () {
  'use strict';

  var WIDTH_RE = /width/i;

  function text(el) { return (el && el.textContent || '').trim(); }

  function getProductData(section) {
    var node = section.querySelector('[data-product-json]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }

  // Judge stock by QUANTITY when inventory is tracked, falling back to
  // `available`. This deliberately does NOT rely on inventory_policy
  // (Rithum may push "continue selling", which keeps `available` true at
  // 0 qty — the exact case that makes an in-stock listing look sold out).
  function isInStock(v) {
    if (!v) return false;
    if (v.inventory_management && typeof v.inventory_quantity === 'number') {
      return v.inventory_quantity > 0;
    }
    return v.available === true;
  }

  /* ---- 1. AUTO-SELECT FIRST IN-STOCK VARIANT -------------------------- */
  function autoSelect(section, product) {
    if (window.TLD_DISABLE_AUTOSELECT) return;
    if (section.getAttribute('data-tld-autoselect') === 'off') return;
    if (!product || !product.variants || !product.variants.length) return;

    var idField = section.querySelector('[name="id"][data-product-select], [data-product-select]');
    var currentId = idField ? parseInt(idField.value, 10) : null;

    var current = null, i;
    for (i = 0; i < product.variants.length; i++) {
      if (product.variants[i].id === currentId) { current = product.variants[i]; break; }
    }
    // Already showing an in-stock variant → nothing to do.
    if (current && isInStock(current)) return;

    var target = null;
    for (i = 0; i < product.variants.length; i++) {
      if (isInStock(product.variants[i])) { target = product.variants[i]; break; }
    }
    if (!target) return;                 // genuinely all sold out
    if (current && target.id === current.id) return;

    applyVariant(section, target);
  }

  // Set each option selector to the target variant's option values and
  // fire the events theme.js expects.
  function applyVariant(section, variant) {
    var options = [variant.option1, variant.option2, variant.option3];
    var changed = false;

    for (var pos = 1; pos <= 3; pos++) {
      var value = options[pos - 1];
      if (value === null || typeof value === 'undefined') continue;

      // Radios / swatches for this option position
      var radios = section.querySelectorAll(
        'input[type="radio"][data-single-option-selector][data-index="option' + pos + '"]'
      );
      if (radios.length) {
        for (var r = 0; r < radios.length; r++) {
          var match = radios[r].value === value;
          if (match && !radios[r].checked) { radios[r].checked = true; fire(radios[r]); changed = true; }
          else if (match) { radios[r].checked = true; }
        }
        continue;
      }

      // Select-popout (hidden input + visible text list)
      var hidden = section.querySelector(
        'input[type="hidden"][data-single-option-selector][data-index="option' + pos + '"]'
      );
      if (hidden && hidden.value !== value) {
        hidden.value = value;
        syncPopout(hidden, value);
        fire(hidden);
        changed = true;
      }
    }
    return changed;
  }

  // Keep the custom select-popout's visible label + current item in sync
  function syncPopout(hidden, value) {
    var popout = hidden.closest('[data-popout]');
    if (!popout) return;
    var label = popout.querySelector('[data-popout-text]');
    if (label) label.textContent = value;
    var items = popout.querySelectorAll('[data-popout-option]');
    for (var i = 0; i < items.length; i++) {
      var on = items[i].getAttribute('data-value') === value;
      if (on) { items[i].setAttribute('aria-current', 'true'); }
      else { items[i].removeAttribute('aria-current'); }
      var li = items[i].closest('.select-popout__item');
      if (li) li.classList.toggle('select-popout__item--current', on);
    }
  }

  function fire(el) {
    el.dispatchEvent(new Event('change', { bubbles: true }));
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /* ---- 2. WIDTH-FIRST ORDERING --------------------------------------- */
  function widthFirst(section) {
    var stacks = section.querySelectorAll('.product__selectors');
    for (var s = 0; s < stacks.length; s++) {
      var stack = stacks[s];
      var wrappers = stack.querySelectorAll(':scope > .selector-wrapper');
      for (var w = 0; w < wrappers.length; w++) {
        var legend = wrappers[w].querySelector('.radio__legend__label, .select__label, legend');
        if (legend && WIDTH_RE.test(text(legend))) {
          if (stack.firstElementChild !== wrappers[w]) {
            stack.insertBefore(wrappers[w], stack.firstElementChild);
          }
          break;
        }
      }
    }
  }

  /* ---- 2b. HIDE SINGLE-VALUE OPTIONS --------------------------------- */
  // A lone "Width: Medium" (or any option with one value) is noise — hide it.
  function hideSingleOptions(section) {
    var wrappers = section.querySelectorAll('.product__selectors > .selector-wrapper');
    for (var i = 0; i < wrappers.length; i++) {
      var w = wrappers[i];
      if (!w.querySelector('[data-single-option-selector]')) continue; // skip qty, etc.
      var radios = w.querySelectorAll('input[type="radio"][data-single-option-selector]');
      var count = radios.length ? radios.length
                                : w.querySelectorAll('[data-popout-option]').length;
      if (count === 1) w.classList.add('tld-option-hidden');
    }
  }

  /* ---- 3. SELECTED COLOR NAME ---------------------------------------- */
  function colorName(section) {
    // Opt-in via the product section setting (data-tld-swatch-name="on").
    if (section.getAttribute('data-tld-swatch-name') !== 'on') return;
    var groups = section.querySelectorAll('[data-swatches-container]');
    for (var g = 0; g < groups.length; g++) {
      (function (group) {
        var legend = group.querySelector('[data-swatches-label], .radio__legend');
        if (!legend) return;
        var out = legend.querySelector('.tld-swatch-selected');
        if (!out) {
          out = document.createElement('span');
          out.className = 'tld-swatch-selected';
          legend.appendChild(out);
        }
        var update = function () {
          var checked = group.querySelector('input[type="radio"]:checked');
          out.textContent = checked ? checked.value : '';
        };
        group.addEventListener('change', update);
        update();
      })(groups[g]);
    }
  }

  /* ---- 4. MOBILE STICKY ADD-TO-CART BAR ------------------------------ */
  function stickyBar(section) {
    var bar = section.querySelector('[data-tld-sticky-atc]');
    if (!bar) return;
    var realBtn = section.querySelector('[data-add-to-cart]');
    if (!realBtn) return;

    var stickyBtn = bar.querySelector('[data-tld-sticky-btn]');
    var stickyLabel = bar.querySelector('[data-tld-sticky-label]');
    var stickyPrice = bar.querySelector('[data-tld-sticky-price]');
    var realPrice = section.querySelector('[data-product-price]');
    var realLabel = realBtn.querySelector('[data-add-to-cart-text]');

    // The sticky button reuses the real ATC (variant state, AJAX, cart drawer).
    stickyBtn.addEventListener('click', function () {
      if (realBtn.disabled) return;
      realBtn.click();
    });

    function syncBtn() {
      var disabled = realBtn.disabled || realBtn.getAttribute('aria-disabled') === 'true';
      stickyBtn.disabled = disabled;
      bar.classList.toggle('is-soldout', disabled);
      if (stickyLabel && realLabel) stickyLabel.textContent = realLabel.textContent.trim();
    }
    function syncPrice() {
      if (stickyPrice && realPrice) stickyPrice.innerHTML = realPrice.innerHTML;
    }
    syncBtn();

    // Mirror live updates theme.js makes to the button + price on variant change.
    if (window.MutationObserver) {
      new MutationObserver(syncBtn).observe(realBtn, {
        subtree: true, childList: true, characterData: true,
        attributes: true, attributeFilter: ['disabled', 'aria-disabled']
      });
      if (realPrice) {
        new MutationObserver(syncPrice).observe(realPrice, {
          subtree: true, childList: true, characterData: true
        });
      }
    }

    // Show the bar only once the real ATC has scrolled out of view.
    var sentinel = section.querySelector('.product__submit__holder') || realBtn;
    if ('IntersectionObserver' in window && sentinel) {
      new IntersectionObserver(function (entries) {
        var show = !entries[0].isIntersecting;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      }, { threshold: 0 }).observe(sentinel);
    }
  }

  /* ---- init ---------------------------------------------------------- */
  function initSection(section) {
    if (!section || section.__tldInit) return;
    section.__tldInit = true;
    // Master gate: everything stays dormant until the product section opts in
    // (data-tld-optimized="on"), set only by the optimized template. This lets
    // the files live in production safely and be tested per-template.
    if (section.getAttribute('data-tld-optimized') !== 'on') return;
    var product = getProductData(section);
    widthFirst(section);     // reorder before auto-select so UI is stable
    hideSingleOptions(section);
    autoSelect(section, product);
    colorName(section);
    stickyBar(section);
  }

  function initAll(root) {
    var scope = root || document;
    // Include the scope element itself (shopify:section:load passes the section).
    if (scope.getAttribute && scope.getAttribute('data-section-type') === 'product-template') {
      initSection(scope);
    }
    var sections = scope.querySelectorAll('[data-section-type="product-template"]');
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor / dynamic section loads
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
